// Integrations — real page.
// Google Calendar/Meet is a live integration; the rest remain "Soon"
// placeholders until the next integration wave is approved.
import { useEffect, useState } from "react";
import NeedsReview from "@/components/integrations/NeedsReview";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Calendar, CheckCircle2, Loader2, Lock, Plug, Plus, RefreshCcw,
  ShieldAlert, XCircle,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const PLACEHOLDER = [
  { name: "Notion", desc: "Sync project docs & knowledge into portal pages.", color: "#000000" },
  { name: "Google Drive", desc: "Automatically surface files clients need to see.", color: "#0F9D58" },
  { name: "ClickUp", desc: "Bring task progress into portal timelines.", color: "#7B68EE" },
  { name: "Stripe", desc: "Show invoices, payments and receipts privately.", color: "#635BFF" },
  { name: "Figma", desc: "Embed live design frames for review.", color: "#F24E1E" },
  { name: "Slack", desc: "Post portal activity into a shared channel.", color: "#4A154B" },
];

function StatusPill({ status }) {
  if (status === "connected") return (
    <span data-testid="google-status-connected"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
      <CheckCircle2 className="w-3 h-3" /> Connected
    </span>
  );
  if (status === "expired" || status === "error") return (
    <span data-testid="google-status-attention"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
      <ShieldAlert className="w-3 h-3" /> Reconnect needed
    </span>
  );
  return (
    <span data-testid="google-status-disconnected"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">
      Not connected
    </span>
  );
}

export default function Integrations() {
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get("/integrations");
      setConn(data?.google_calendar || null);
    } catch { setConn(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (params.get("connected") === "1" && params.get("integration") === "google_calendar") {
      toast.success("Google Calendar connected — syncing your meetings now");
      setParams({}, { replace: true });
      load();
    } else if (params.get("integration") === "google_calendar" && params.get("error")) {
      const code = params.get("error");
      const HUMAN = {
        access_denied: "You didn't grant Google access. No problem — you can try again anytime.",
        invalid_state: "That connection link expired. Please click Connect Google again.",
        invalid_scope: "Google refused the requested permissions. Please retry.",
        missing_params: "Google didn't send back the required information. Please retry.",
        callback_failed: "We couldn't finish the Google connection. Please try again.",
      };
      toast.error(HUMAN[code] || `Google connection failed (${code})`);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/integrations/google/connect");
      if (data?.authorize_url) window.location.href = data.authorize_url;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not start Google connection";
      toast.error(detail);
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post("/integrations/google/disconnect");
      toast.success("Google Calendar disconnected");
      await load();
    } catch { toast.error("Could not disconnect"); }
    finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/google/sync");
      if (data?.ok) {
        const parts = [];
        parts.push(`Synced ${data.events_seen || 0} event${data.events_seen === 1 ? "" : "s"}`);
        if (data.new_meetings) parts.push(`${data.new_meetings} new`);
        if (data.newly_matched) parts.push(`${data.newly_matched} matched to a client`);
        toast.success(parts.join(" · "));
      } else toast.error(`Sync failed: ${data?.reason || "unknown"}`);
      await load();
    } catch { toast.error("Sync failed"); }
    finally { setBusy(false); }
  };

  const status = conn?.status || "not_connected";
  const configured = conn?.configured !== false;

  return (
    <AppShell breadcrumbs={["Integrations"]}>
      <div className="max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="overline mb-2">Integrations</p>
            <h1 className="font-display text-4xl tracking-tight text-slate-900">
              Bring your tools in
            </h1>
            <p className="text-slate-500 mt-2 max-w-2xl">
              Your existing workflow stays untouched. ClientSpace listens to your tools and turns them into intelligence your clients love.
            </p>
          </div>
        </div>

        {/* Google Calendar / Meet — LIVE integration */}
        <div data-testid="google-integration-card"
             className="rounded-xl border border-slate-200 bg-white p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-[#1a73e8]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="font-medium text-slate-900">Google Calendar &amp; Meet</p>
                {loading ? (
                  <span className="text-[11px] text-slate-400">Loading…</span>
                ) : <StatusPill status={status} />}
              </div>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                Sync client meetings into ClientSpace. Attendee emails are matched to clients automatically.
                Meetings surface as insight cards in the Attention Center.
              </p>
              {!configured && (
                <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                     data-testid="google-not-configured">
                  Google integration requires backend configuration.
                  Add <code className="bg-white px-1 rounded">GOOGLE_CLIENT_ID</code> and
                  <code className="bg-white px-1 rounded ml-1">GOOGLE_CLIENT_SECRET</code> to
                  <code className="bg-white px-1 rounded ml-1">backend/.env</code>, then reload.
                </div>
              )}
              {status === "connected" && (
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                  {conn?.google_email && (
                    <span data-testid="google-account">Account: <b className="text-slate-700">{conn.google_email}</b></span>
                  )}
                  {conn?.last_sync_at && (
                    <span data-testid="google-last-sync">
                      Last sync {formatDistanceToNow(new Date(conn.last_sync_at), { addSuffix: true })}
                    </span>
                  )}
                </div>
              )}
              {(status === "expired" || status === "error") && conn?.last_error && (
                <p className="mt-2 text-xs text-amber-800">{conn.last_error}</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              {status === "not_connected" && (
                <button data-testid="google-connect-btn"
                        onClick={connect} disabled={busy || !configured}
                        className="text-sm font-medium text-white bg-[#C55A46] hover:bg-[#B4523E] disabled:bg-slate-300 rounded-full px-4 py-1.5 inline-flex items-center gap-1.5 transition-colors">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  Connect Google
                </button>
              )}
              {(status === "expired" || status === "error") && (
                <button data-testid="google-reconnect-btn"
                        onClick={connect} disabled={busy}
                        className="text-sm font-medium text-slate-900 border border-slate-200 hover:border-slate-300 rounded-full px-4 py-1.5 inline-flex items-center gap-1.5">
                  <RefreshCcw className="w-3.5 h-3.5" /> Reconnect
                </button>
              )}
              {status === "connected" && (
                <>
                  <button data-testid="google-sync-btn"
                          onClick={syncNow} disabled={busy}
                          className="text-xs font-medium text-slate-700 border border-slate-200 hover:border-slate-300 rounded-full px-3 py-1 inline-flex items-center gap-1.5">
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
                    Sync now
                  </button>
                  <button data-testid="google-disconnect-btn"
                          onClick={disconnect} disabled={busy}
                          className="text-xs text-slate-500 hover:text-rose-600 inline-flex items-center gap-1">
                    <XCircle className="w-3 h-3" /> Disconnect
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <NeedsReview refreshKey={conn?.last_sync_at} />

        <p className="overline mb-3">More integrations</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PLACEHOLDER.map((it) => (
            <div key={it.name}
                 data-testid={`integration-card-${it.name.toLowerCase().replace(/\s+/g, "-")}`}
                 className="rounded-xl border border-slate-200 bg-white p-5 opacity-90">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-semibold text-xs"
                     style={{ backgroundColor: it.color }}>
                  {it.name[0]}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 text-sm">{it.name}</p>
                  <p className="text-xs text-slate-400">Not connected</p>
                </div>
                <button disabled className="text-[11px] font-medium text-slate-400 border border-slate-200 rounded-full px-2.5 py-1 inline-flex items-center gap-1 cursor-not-allowed">
                  <Lock className="w-3 h-3" /> Soon
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-3 leading-relaxed">{it.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center">
          <div className="w-10 h-10 rounded-lg bg-[#FEF2F2] flex items-center justify-center mx-auto mb-3">
            <Plug className="w-4 h-4 text-[#C55A46]" />
          </div>
          <p className="font-display text-lg text-slate-900">Need a specific tool?</p>
          <p className="text-sm text-slate-500 mt-1">
            Tell us what your workflow depends on — we prioritize what agencies actually use.
          </p>
          <button data-testid="request-integration-btn"
                  className="mt-3 text-sm font-medium border border-slate-200 rounded-lg px-4 py-2 hover:border-slate-300 transition-colors">
            Request an integration
          </button>
        </div>
      </div>
    </AppShell>
  );
}
